import React, { useCallback, useState } from 'react';
import {Box, Typography} from '@mui/material';
import {UseFormWatch, UseFormSetValue, FieldErrors} from 'react-hook-form';
import LabelCheckbox from './LabelCheckbox';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrCheckbox from '../../../common/arr-checkbox/ArrCheckbox';
import {FormValues, SourcesState} from '../schema';
import {ISelectItem} from '../../../../shared/types';
import {t} from 'i18next';
import { ToastType } from '../../../../shared/constants';

interface Props {
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  setSourceSelectionDATError: React.Dispatch<React.SetStateAction<boolean>>;
  errors: FieldErrors<FormValues>;
  loadFiles?: ISelectItem[];
  headersData?: ISelectItem[];
  linkType?: ISelectItem[];
  disabled?: boolean;
}

const SourcesSection: React.FC<Props> = ({
  watch,
  setValue,
  errors,
  setSourceSelectionDATError,
  loadFiles = [],
  headersData = [],
  linkType = [],
  disabled = false,
}) => {
  const sources = watch('sources') as SourcesState;

  const toggleSource = (key: keyof SourcesState) => {
    if (key === 'dat') return;
    const current = sources[key];
    const nextEnabled = !current.enabled;
    setValue(`sources.${key}.enabled` as any, nextEnabled, {shouldDirty: true});
    if (!nextEnabled) {
      setValue(`sources.${key}.loadFile` as any, null, {shouldDirty: true});
      setValue(`sources.${key}.isPathInDatFile` as any, false, {
        shouldDirty: true,
      });
      setValue(`sources.${key}.filePathField` as any, null, {
        shouldDirty: true,
      });
    }
  };

  const toggleDat = () => {
    // Only prevent when entire pane is globally disabled
    if (disabled) return;
    const dat = sources.dat;
    const nextEnabled = !dat.enabled;
    setValue('sources.dat.enabled', nextEnabled, {shouldDirty: true});
    if (!nextEnabled) {
      setValue('sources.dat.loadFile', null, {shouldDirty: true});
      setValue('sources.dat.documentKey', null, {shouldDirty: true});
    } else {
      // Auto-select first load file if available (keep previous behavior)
      if (!dat.loadFile && loadFiles.length > 0) {
        setValue('sources.dat.loadFile', loadFiles[0], {shouldDirty: true});
      }
      // Do NOT auto-populate document key (user must choose)
    }
  };

  const setLoadFile = (key: keyof SourcesState, val: any) => {
    setValue(`sources.${key}.loadFile` as any, val, {shouldDirty: true});
  };

  const setDocumentKey = (val: any) => {
    setValue('sources.dat.documentKey' as any, val, {shouldDirty: true});
  };

  const setPathInDatFile = (key: keyof SourcesState, checked: boolean) => {
    // Only allow the selection `Is Path in DAT File?` checkbox when DAT source is selected
    const datEnabled = sources.dat.enabled;
    if (datEnabled)
    {
        setValue(`sources.${key}.isPathInDatFile` as any, checked, {
        shouldDirty: true,
      });
      if (!checked) {
        setValue(`sources.${key}.filePathField` as any, null, {
          shouldDirty: true,
          });
        }
    }
    else {
      setSourceSelectionDATError(true);
    }
  };

  const setFilePathField = (key: keyof SourcesState, val: any) => {
    setValue(`sources.${key}.filePathField` as any, val, {shouldDirty: true});
  };

  const datError = (errors?.sources as any)?.dat?.enabled?.message;

  // Render DAT source
  const renderDat = () => {
    const dat = sources.dat;
    return (
      <React.Fragment key="dat">
        <Box className="source-item-wrapper">
          <LabelCheckbox
            label="DAT"
            checked={dat.enabled}
            onChange={toggleDat}
            disabled={disabled} // Only lock when pane disabled
            ariaChecked={dat.enabled ? 'true' : 'false'}
            aria-label={t(
              'ingestions.Wizard.ariaLabels.sourcesSection.datCheckbox'
            )}
          />
        </Box>
        {datError && (
          <Typography
            className="error-text"
            style={{marginLeft: '2rem'}}
            data-testid={`sources-section-dat-error`}
          >
            {datError}
          </Typography>
        )}
        {dat.enabled && (
          <Box
            className="source-fields-container"
            aria-labelledby="dat-source-checkbox"
          >
            <ArrSingleSelectDropdown
              dropdownLabel="Load File*"
              searchable
              dropdownItems={loadFiles}
              dropdownItemDisplay="key"
              dropdownItemKey="key"
              selectedValues={dat.loadFile}
              onSelectionChange={(val) => setLoadFile('dat', val)}
              disabled={disabled}
              error={dat.enabled && !dat.loadFile}
              errorText={
                dat.enabled && !dat.loadFile
                  ? 'Load File is required'
                  : undefined
              }
              dataTestId="asd-dat-load-file-dropdown"
              selectRestProps={{
                'aria-label': t(
                  'ingestions.Wizard.ariaLabels.sourcesSection.datLoadFileDropdown'
                ),
                'aria-required': dat.enabled ? true : false,
                'aria-invalid': dat.enabled && !dat.loadFile,
                'aria-describedby':
                  dat.enabled && !dat.loadFile
                    ? 'dat-load-file-error'
                    : undefined,
              }}
              inputLabelProps={{
                'aria-label': 'Load File, required',
              }}
              tabIndex={-1}
            />
            <ArrSingleSelectDropdown
              dropdownLabel="Document Key*"
              searchable
              dropdownItems={headersData}
              dropdownItemDisplay="displayValue"
              dropdownItemKey="key"
              selectedValues={dat.documentKey}
              onSelectionChange={(val) => setDocumentKey(val)}
              disabled={disabled}
              error={dat.enabled && !dat.documentKey}
              errorText={
                dat.enabled && !dat.documentKey
                  ? 'Document Key is required'
                  : undefined
              }
              dataTestId="asd-dat-document-key-dropdown"
              selectRestProps={{
                'aria-label': t(
                  'ingestions.Wizard.ariaLabels.sourcesSection.datDocumentKeyDropdown'
                ),
                'aria-required': dat.enabled ? true : false,
                'aria-invalid': dat.enabled && !dat.documentKey,
                'aria-describedby':
                  dat.enabled && !dat.documentKey
                    ? 'dat-document-key-error'
                    : undefined,
              }}
              inputLabelProps={{
                'aria-label': 'Document Key, required',
              }}
              tabIndex={-1}
            />
          </Box>
        )}
      </React.Fragment>
    );
  };

  // Render Native source
  const renderNative = () => {
    const native = sources.native;
    return (
      <React.Fragment key="native">
        <Box className="source-item-wrapper">
          <LabelCheckbox
            label="Native"
            checked={native.enabled}
            onChange={() => toggleSource('native')}
            disabled={disabled}
            ariaChecked={native.enabled ? 'true' : 'false'}
            aria-label={t(
              'ingestions.Wizard.ariaLabels.sourcesSection.nativeCheckbox'
            )}
          />
        </Box>
        {native.enabled && (
          <>
            <Box className="source-fields-container">
              <ArrSingleSelectDropdown
                dropdownLabel="Load File*"
                searchable
                dropdownItems={loadFiles}
                dropdownItemDisplay="key"
                dropdownItemKey="key"
                selectedValues={native.loadFile}
                onSelectionChange={(val) => setLoadFile('native', val)}
                disabled={disabled || native.isPathInDatFile}
                error={
                  native.enabled && !native.loadFile && !native.isPathInDatFile
                }
                errorText={
                  native.enabled && !native.loadFile && !native.isPathInDatFile
                    ? 'Load File is required'
                    : undefined
                }
                dataTestId="asd-native-load-file-dropdown"
                selectRestProps={{
                  'aria-label': t(
                    'ingestions.Wizard.ariaLabels.sourcesSection.nativeLoadFileDropdown'
                  ),
                  'aria-required': native.enabled ? true : false,
                  'aria-invalid':
                    native.enabled &&
                    !native.loadFile &&
                    !native.isPathInDatFile,
                  'aria-describedby':
                    native.enabled &&
                    !native.loadFile &&
                    !native.isPathInDatFile
                      ? 'native-load-file-error'
                      : undefined,
                }}
                inputLabelProps={{
                  'aria-label': 'Load File, required',
                }}
                tabIndex={-1}
              />
            </Box>

            <Box className="source-checkbox-wrapper">
              <ArrCheckbox
                label={t('ingestions.Wizard.sourcesSection.checkBoxTitle')}
                checked={!!native.isPathInDatFile}
                size="medium"
                onChange={(checked) => setPathInDatFile('native', checked)}
                disabled={disabled}
                inputProps={{
                  'aria-checked': native?.isPathInDatFile ? 'true' : 'false',
                }}
                aria-label={t(
                  'ingestions.Wizard.ariaLabels.sourcesSection.nativePathInDatFileCheckbox'
                )}
              />
              <Typography
                className="checkbox-label"
                data-testid={`sources-section-check-box-title`}
              >
                {t('ingestions.Wizard.sourcesSection.checkBoxTitle')}
              </Typography>
            </Box>

            {native.isPathInDatFile && (
              <Box className="source-fields-container">
                <ArrSingleSelectDropdown
                  searchable
                  dropdownLabel="File Path Field in DAT*"
                  dropdownItems={headersData}
                  dropdownItemDisplay="displayValue"
                  dropdownItemKey="key"
                  selectedValues={native.filePathField ?? null}
                  onSelectionChange={(val) => setFilePathField('native', val)}
                  disabled={disabled}
                  error={native.isPathInDatFile && !native.filePathField}
                  errorText={
                    native.isPathInDatFile && !native.filePathField
                      ? 'File Path Field in DAT is required'
                      : undefined
                  }
                  dataTestId="asd-native-file-path-field-dropdown"
                  selectRestProps={{
                    'aria-label': t(
                      'ingestions.Wizard.ariaLabels.sourcesSection.nativeFilePathDropdown'
                    ),
                  }}
                  inputLabelProps={{
                    'aria-label': 'File Path Field in DAT, required',
                  }}
                  tabIndex={-1}
                />
              </Box>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  // Render simple source
  const renderSimpleSource = (key: keyof SourcesState, label: string) => {
    if (key === 'dat' || key === 'native' || key === 'tiff' || key === 'other')
      return null;
    const s = sources[key];
    return (
      <React.Fragment key={key}>
        <Box className="source-item-wrapper">
          <LabelCheckbox
            label={label}
            checked={s.enabled}
            onChange={() => toggleSource(key)}
            disabled={disabled}
            aria-label={t(
              `ingestions.Wizard.ariaLabels.sourcesSection.${key}SourceCheckbox`
            )}
            ariaChecked={s.enabled ? 'true' : 'false'}
          />
        </Box>
        {s.enabled && (
          <>
            <Box className="source-fields-container">
              <ArrSingleSelectDropdown
                searchable
                dropdownLabel="Load File*"
                dropdownItems={loadFiles}
                dropdownItemDisplay="key"
                dropdownItemKey="key"
                selectedValues={s.loadFile}
                onSelectionChange={(val) => setLoadFile(key, val)}
                disabled={disabled || s.isPathInDatFile}
                error={s.enabled && !s.loadFile && !s.isPathInDatFile}
                errorText={
                  s.enabled && !s.loadFile && !s.isPathInDatFile
                    ? 'Load File is required'
                    : undefined
                }
                dataTestId={`${key}-asd-load-file-dropdown`}
                selectRestProps={{
                  'aria-label': ` ${t(`ingestions.Wizard.ariaLabels.sourcesSection.${key}LoadFileDropdown`)}`,
                  'aria-invalid':
                    s.enabled && !s.loadFile && !s.isPathInDatFile,
                }}
                tabIndex={-1}
                inputLabelProps={{
                  'aria-label': 'Load File, required',
                }}
              />
            </Box>

            <Box className="source-checkbox-wrapper">
              <ArrCheckbox
                label={t('ingestions.Wizard.sourcesSection.checkBoxTitle')}
                checked={!!s.isPathInDatFile}
                size="medium"
                onChange={(checked) => setPathInDatFile(key, checked)}
                disabled={disabled}
                aria-label={t(
                  `ingestions.Wizard.ariaLabels.sourcesSection.${key}PathInDatFileCheckbox`
                )}
                inputProps={{
                  'aria-checked': s?.isPathInDatFile ? 'true' : 'false',
                }}
              />
              <Typography
                className="checkbox-label"
                data-testid={`sources-section-check-box-title-text`}
              >
                {t('ingestions.Wizard.sourcesSection.checkBoxTitle')}
              </Typography>
            </Box>

            {s.isPathInDatFile && (
              <Box className="source-fields-container">
                <ArrSingleSelectDropdown
                  dropdownLabel="File Path Field in DAT*"
                  searchable
                  dropdownItems={headersData}
                  dropdownItemDisplay="displayValue"
                  dropdownItemKey="key"
                  selectedValues={s.filePathField ?? null}
                  onSelectionChange={(val) => setFilePathField(key, val)}
                  disabled={disabled}
                  error={s.isPathInDatFile && !s.filePathField}
                  errorText={
                    s.isPathInDatFile && !s.filePathField
                      ? 'File Path Field in DAT is required'
                      : undefined
                  }
                  dataTestId={`${key}-asd-file-path-field-dropdown`}
                  selectRestProps={{
                    'aria-label': t(
                      `ingestions.Wizard.ariaLabels.sourcesSection.${key}FilePathFieldDropdown`
                    ),
                  }}
                  tabIndex={-1}
                  inputLabelProps={{
                    'aria-label': 'File Path Field in DAT, required',
                  }}
                />
              </Box>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  // Render TIFF source
  const renderTiff = () => {
    const tiff = sources.tiff;
    return (
      <React.Fragment key="tiff">
        <Box className="source-item-wrapper">
          <LabelCheckbox
            label="TIFF"
            checked={tiff.enabled}
            onChange={() => toggleSource('tiff')}
            disabled={disabled}
            aria-label={t(
              'ingestions.Wizard.ariaLabels.sourcesSection.tiffSourceCheckbox'
            )}
            ariaChecked={tiff.enabled ? 'true' : 'false'}
          />
        </Box>
        {tiff.enabled && (
          <>
            <Box className="source-fields-container">
              <ArrSingleSelectDropdown
                dropdownLabel="Load File*"
                searchable
                dropdownItems={loadFiles}
                dropdownItemDisplay="key"
                dropdownItemKey="key"
                selectedValues={tiff.loadFile}
                onSelectionChange={(val) => setLoadFile('tiff', val)}
                disabled={disabled || tiff.isPathInDatFile}
                error={tiff.enabled && !tiff.loadFile && !tiff.isPathInDatFile}
                errorText={
                  tiff.enabled && !tiff.loadFile && !tiff.isPathInDatFile
                    ? 'Load File is required'
                    : undefined
                }
                dataTestId="asd-tiff-load-file-dropdown"
                selectRestProps={{
                  'aria-label': t(
                    'ingestions.Wizard.ariaLabels.sourcesSection.tiffLoadFileDropdown'
                  ),
                }}
                inputLabelProps={{
                  'aria-label': 'Load File, required',
                }}
                tabIndex={-1}
              />
            </Box>

            <Box className="source-checkbox-row">
              <Box className="checkbox-group">
                <ArrCheckbox
                  label={t('ingestions.Wizard.sourcesSection.checkBoxTitle')}
                  checked={!!tiff.isPathInDatFile}
                  size="medium"
                  onChange={(checked) => setPathInDatFile('tiff', checked)}
                  disabled={disabled}
                  aria-label={t(
                    'ingestions.Wizard.ariaLabels.sourcesSection.tiffPathInDatFileCheckbox'
                  )}
                  inputProps={{
                    'aria-checked': tiff?.isPathInDatFile ? 'true' : 'false',
                  }}
                />
                <Typography
                  className="checkbox-label"
                  data-testid={`sources-section-check-box-title-btn`}
                >
                  {t('ingestions.Wizard.sourcesSection.checkBoxTitle')}
                </Typography>
              </Box>

              <Box className="checkbox-group-flex">
                <ArrCheckbox
                  label="Generate Searchable PDFs"
                  checked={tiff.generateSearchablePdfs ?? false}
                  size="medium"
                  onChange={(checked) => {
                    setValue('sources.tiff.generateSearchablePdfs', checked, {
                      shouldDirty: true,
                    });
                  }}
                  disabled={disabled}
                  aria-label={t(
                    'ingestions.Wizard.ariaLabels.sourcesSection.tiffGenerateSearchablePdfsCheckbox'
                  )}
                  inputProps={{
                    'aria-checked': tiff?.generateSearchablePdfs
                      ? 'true'
                      : 'false',
                  }}
                />
                <Typography
                  className="checkbox-label"
                  data-testid={`sources-section-generate-searchable-pdfs`}
                >
                  Generate Searchable PDFs
                </Typography>
              </Box>
            </Box>

            {tiff.isPathInDatFile && (
              <Box className="source-fields-container">
                <ArrSingleSelectDropdown
                  dropdownLabel="File Path Field in DAT*"
                  searchable
                  dropdownItems={headersData}
                  dropdownItemDisplay="displayValue"
                  dropdownItemKey="key"
                  selectedValues={tiff.filePathField ?? null}
                  onSelectionChange={(val) => setFilePathField('tiff', val)}
                  disabled={disabled}
                  error={tiff.isPathInDatFile && !tiff.filePathField}
                  errorText={
                    tiff.isPathInDatFile && !tiff.filePathField
                      ? 'File Path Field in DAT is required'
                      : undefined
                  }
                  dataTestId="asd-tiff-file-path-field-dropdown"
                  selectRestProps={{
                    'aria-label': t(
                      'ingestions.Wizard.ariaLabels.sourcesSection.tiffFilePathFieldDropdown'
                    ),
                  }}
                  tabIndex={-1}
                  inputLabelProps={{
                    'aria-label': 'File Path Field in DAT, required',
                  }}
                />
              </Box>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  // Render Other source
  const renderOther = () => {
    const other = sources.other;
    return (
      <React.Fragment key="other">
        <Box className="source-item-wrapper">
          <LabelCheckbox
            label="Other"
            checked={other.enabled}
            onChange={() => toggleSource('other')}
            disabled={disabled}
            aria-label={t(
              'ingestions.Wizard.ariaLabels.sourcesSection.otherSourceCheckbox'
            )}
            ariaChecked={other.enabled ? 'true' : 'false'}
          />
        </Box>
        {other.enabled && (
          <>
            <Box className="source-fields-container">
              <ArrSingleSelectDropdown
                dropdownLabel="Link Type"
                searchable
                dropdownItems={linkType}
                dropdownItemDisplay="displayValue"
                dropdownItemKey="key"
                selectedValues={(other as any).linkType ?? null}
                onSelectionChange={(val) => {
                  // ✅ Update form state when selection changes
                  setValue('sources.other.linkType' as any, val, {
                    shouldDirty: true,
                  });
                }}
                disabled={disabled}
                dataTestId="asd-link-type-dropdown"
                tabIndex={-1}
                inputLabelProps={{
                  'aria-label': 'Link Type',
                }}
              />

              <ArrSingleSelectDropdown
                dropdownLabel="Load File*"
                searchable
                dropdownItems={loadFiles}
                dropdownItemDisplay="key"
                dropdownItemKey="key"
                selectedValues={other.loadFile}
                onSelectionChange={(val) => setLoadFile('other', val)}
                disabled={disabled || other.isPathInDatFile}
                error={
                  other.enabled && !other.loadFile && !other.isPathInDatFile
                }
                errorText={
                  other.enabled && !other.loadFile && !other.isPathInDatFile
                    ? 'Load File is required'
                    : undefined
                }
                dataTestId="asd-other-load-file-dropdown"
                selectRestProps={{
                  'aria-label': t(
                    'ingestions.Wizard.ariaLabels.sourcesSection.otherLoadFileDropdown'
                  ),
                  'aria-required': other.enabled ? 'true' : 'false',
                  'aria-invalid':
                    other.enabled && !other.loadFile && !other.isPathInDatFile,
                  'aria-describedby':
                    other.enabled && !other.loadFile && !other.isPathInDatFile
                      ? 'other-load-file-error'
                      : undefined,
                }}
                inputLabelProps={{
                  'aria-label': 'Load File, required',
                }}
                tabIndex={-1}
              />
            </Box>

            <Box className="source-checkbox-wrapper">
              <ArrCheckbox
                label={t('ingestions.Wizard.sourcesSection.checkBoxTitle')}
                checked={!!other.isPathInDatFile}
                size="medium"
                onChange={(checked) => setPathInDatFile('other', checked)}
                disabled={disabled}
                aria-label={t(
                  'ingestions.Wizard.ariaLabels.sourcesSection.otherPathInDatFileCheckbox'
                )}
                inputProps={{
                  'aria-checked': other?.isPathInDatFile ? 'true' : 'false',
                }}
              />
              <Typography
                className="checkbox-label"
                data-testid={`sources-section-check-box-title-button`}
              >
                {t('ingestions.Wizard.sourcesSection.checkBoxTitle')}
              </Typography>
            </Box>

            {other.isPathInDatFile && (
              <Box className="source-fields-container">
                <ArrSingleSelectDropdown
                  dropdownLabel="File Path Field in DAT*"
                  searchable
                  dropdownItems={headersData}
                  dropdownItemDisplay="displayValue"
                  dropdownItemKey="key"
                  selectedValues={other.filePathField ?? null}
                  onSelectionChange={(val) => setFilePathField('other', val)}
                  disabled={disabled}
                  error={other.isPathInDatFile && !other.filePathField}
                  errorText={
                    other.isPathInDatFile && !other.filePathField
                      ? 'File Path Field in DAT is required'
                      : undefined
                  }
                  dataTestId="asd-other-file-path-field-dropdown"
                  selectRestProps={{
                    'aria-label': t(
                      'ingestions.Wizard.ariaLabels.sourcesSection.otherFilePathFieldDropdown'
                    ),
                  }}
                  inputLabelProps={{
                    'aria-label': 'File Path Field in DAT, required',
                  }}
                  tabIndex={-1}
                />
              </Box>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box className="section-block" component="section">
      <Typography
        className="section-title typo-text subhead4_size"
        data-testid={`sources-section-title`}
      >
        {t('ingestions.Wizard.sourcesSection.title', {
          defaultValue: 'Select Sources:',
        })}
      </Typography>
      {renderDat()}
      {renderNative()}
      {renderSimpleSource('text', 'Text')}
      {renderSimpleSource('pdf', 'PDF')}
      {renderTiff()}
      {renderSimpleSource('mp3', 'MP3 Variant')}
      {renderSimpleSource('audio', 'Audio Transcript')}
      {renderOther()}
    </Box>
  );
};

export default SourcesSection;
